// ==========================================
// UTILITY FUNCTIONS
// ==========================================
/**
 * Helper to build pagination query params with defaults
 * Ensures pagination params are always included in API requests
 *
 * @param options - Pagination options (optional)
 * @returns URLSearchParams with page and limit always set
 *
 * @example
 * ```typescript
 * async getItems(options?: PaginationOptions): Promise<PaginatedResponseDTO<ItemDTO>> {
 *   const params = buildPaginationParams(options);
 *   // params always includes: page=1&limit=50 (or custom values)
 *
 *   if (options?.sortBy) params.set('sortBy', options.sortBy);
 *   if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
 *
 *   return this.apiClient.get(`/items?${params.toString()}`);
 * }
 * ```
 */
function buildPaginationParams(options) {
    const params = new URLSearchParams();
    // Always include pagination params (defaults)
    params.set('page', String(options?.page ?? 1));
    params.set('limit', String(options?.limit ?? 50));
    if (options?.sortBy)
        params.set('sortBy', options.sortBy);
    if (options?.sortOrder)
        params.set('sortOrder', options.sortOrder);
    return params;
}
/**
 * Type guard to check if response is paginated
 *
 * @param response - Either an array or paginated response
 * @returns True if response is PaginatedResponseDTO
 *
 * @example
 * ```typescript
 * const response = await api.getItems();
 * if (isPaginatedResponse(response)) {
 *   console.log(response.pagination.total);
 * }
 * ```
 */
function isPaginatedResponse(response) {
    return (response !== null &&
        typeof response === 'object' &&
        !Array.isArray(response) &&
        'data' in response &&
        'pagination' in response);
}
/**
 * Extract data array from either array or paginated response
 * Use during hybrid backend phase to safely handle both response shapes
 *
 * @param response - Either an array or paginated response
 * @returns Data array
 *
 * @example
 * ```typescript
 * const response = await api.getItems(); // Could be T[] or PaginatedResponseDTO<T>
 * const items = extractData(response);   // Always T[]
 * ```
 */
function extractData(response) {
    if (Array.isArray(response)) {
        return response;
    }
    return (response?.data ?? []);
}
/**
 * Extract pagination metadata (returns null for array responses)
 *
 * @param response - Either an array or paginated response
 * @returns Pagination metadata or null
 *
 * @example
 * ```typescript
 * const response = await api.getItems();
 * const pagination = extractPagination(response);
 * if (pagination) {
 *   console.log(`Total: ${pagination.total}`);
 * }
 * ```
 */
function extractPagination(response) {
    return isPaginatedResponse(response) ? response.pagination : null;
}
/**
 * Normalize any list response to PaginatedResponseDTO format
 * Converts array responses to paginated format during hybrid backend phase
 *
 * @param response - Either an array or paginated response
 * @returns Paginated response (normalized if input was array)
 *
 * @example
 * ```typescript
 * // Backend returns array (no pagination params)
 * const arrayResponse = await api.getItems();
 * const normalized = normalizeToPaginated(arrayResponse);
 * console.log(normalized.data);       // Original array
 * console.log(normalized.pagination); // Generated metadata
 *
 * // Backend returns paginated (with pagination params)
 * const paginatedResponse = await api.getItems({ page: 1, limit: 50 });
 * const normalized2 = normalizeToPaginated(paginatedResponse);
 * console.log(normalized2); // Same as input (no conversion needed)
 * ```
 */
function normalizeToPaginated(response) {
    if (isPaginatedResponse(response)) {
        return response;
    }
    // Convert array to paginated format
    return {
        data: response,
        pagination: {
            page: 1,
            limit: response.length,
            total: response.length,
            pages: 1,
            hasNext: false,
            hasPrev: false
        }
    };
}
/**
 * Fetch all pages automatically (useful for infinite scroll or complete datasets)
 *
 * ⚠️ WARNING: Use with caution on large datasets. This will make multiple API calls
 * and load all items into memory. Consider using pagination UI instead.
 *
 * @param fetchFn - Function that fetches a page (must return PaginatedResponseDTO)
 * @param limit - Items per page (default: 50)
 * @param maxPages - Safety limit to prevent infinite loops (default: 100)
 * @returns All items from all pages
 *
 * @example
 * ```typescript
 * // Fetch all campaigns (use with caution!)
 * const allCampaigns = await fetchAllPages(
 *   (page, limit) => sdk.campaigns.getCampaigns({ page, limit }),
 *   50,  // items per page
 *   100  // max 100 pages (5000 items)
 * );
 * ```
 */
async function fetchAllPages(fetchFn, limit = 50, maxPages = 100) {
    const allItems = [];
    let page = 1;
    let hasMore = true;
    while (hasMore && page <= maxPages) {
        const response = await fetchFn(page, limit);
        allItems.push(...(response.data || []));
        hasMore = response.pagination.hasNext;
        page++;
    }
    return allItems;
}

export { extractPagination as a, buildPaginationParams as b, extractData as e, fetchAllPages as f, isPaginatedResponse as i, normalizeToPaginated as n };
//# sourceMappingURL=pagination-utils-9vQ-Npkr.js.map
